package com.arena_web.ui.sections.profile.mobile

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodyXlMedium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText

private val CancelBackground =
    "${AppGradients.InteractiveSecondary} padding-box, " +
        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
        "${AppGradients.ButtonBorderGradientPrimary} border-box"

/**
 * Mobile confirmation bottom-sheet — the phone counterpart to the desktop
 * [com.arena_web.ui.components.overlay.FollowConfirmDialog]. Avatar card +
 * heading + optional subtitle + Cancel / blue confirm, laid out inside a
 * [BottomSheet]. Drives the "Unfollow @username" (Figma 8214:286383) and
 * "Remove follower?" (Figma 8214:286660) flows.
 */
@Composable
fun MobileFollowConfirmSheet(
    user: FollowUserDto,
    title: String,
    subtitle: String? = null,
    confirmLabel: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
) {
    val t = LocalAppStrings.current
    BottomSheet(onDismiss = onCancel) {
        Column(
            modifier = Modifier.fillMaxWidth().styleModifier {
                property("gap", s(22))
                property("align-items", "center")
                property("padding-bottom", s(6))
            },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Avatar + heading + subtitle, divided from the actions by a hairline.
            Column(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                ConfirmAvatar(user)
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPaddingBottom(12)
                        .sGap(8)
                        .styleModifier {
                            property("border-bottom", "0.5px solid var(--l-hairline, rgba(255, 255, 255, 0.06))")
                            property("align-items", "center")
                        },
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SpanText(
                        title,
                        modifier = Modifier
                            .bodyXlMedium()
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .textAlign(TextAlign.Center)
                    )
                    if (subtitle != null) {
                        SpanText(
                            subtitle,
                            modifier = Modifier
                                .bodyMRegular()
                                .color(com.arena_web.ui.theme.ThemeVars.Text3)
                                .textAlign(TextAlign.Center)
                        )
                    }
                }
            }

            // Actions — Cancel (outline) + blue confirm, equal width.
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                ConfirmPill(label = t.commonCancel, filled = false, onClick = onCancel)
                ConfirmPill(label = confirmLabel, filled = true, onClick = onConfirm)
            }
        }
    }
}

@Composable
private fun ConfirmAvatar(user: FollowUserDto) {
    val cardFrameUrl = user.cardImageUrl
    Box(
        modifier = Modifier.styleModifier {
            property("flex-shrink", "0")
            property("width", s(40))
            property("height", s(60))
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
        },
        contentAlignment = Alignment.Center
    ) {
        if (cardFrameUrl != null) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = "football",
                size = PlayerCardSize.Highlight,
                cardFrameUrl = cardFrameUrl,
                avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                hideRating = true,
                hideShadow = true,
            )
        } else {
            val avatarUrl = user.avatarUrl
            Box(
                modifier = Modifier
                    .sWidth(40)
                    .sHeight(60)
                    .styleModifier {
                        property("border-radius", s(6))
                        property("background", "rgba(60, 121, 241, 0.15)")
                        property("overflow", "hidden")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    Image(src = avatarUrl, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
                } else {
                    SpanText(
                        user.username.take(1).uppercase(),
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }
        }
    }
}

@Composable
private fun ConfirmPill(label: String, filled: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .flexGrow(1)
            .styleModifier {
                property("height", s(44))
                property("border-radius", s(100))
                property("box-sizing", "border-box")
                if (filled) {
                    property("background", "#0061ff")
                } else {
                    property("border", "0.3px solid transparent")
                    property("background", CancelBackground)
                }
            }
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier
                .bodyLMedium()
                .color(if (filled) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}
